import { MealPlatingQueue } from '@seen/core/models/meal-queues/MealPlatingQueue'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button } from '@seen/ui'
import { IconBowlChopsticks } from '@tabler/icons-react'
import { type JSX, useCallback, useState } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface AddToMealQueueProps {
  participant: Participant
  isDisabled?: boolean
}

export function AddToMealQueue({
  participant,
  isDisabled = false,
}: AddToMealQueueProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const medplum = useMedplum()

  const { data: queues, refetch } = useSearch(
    MealPlatingQueue,
    MealPlatingQueue.currentQueueQuery()
  )

  const handleAddToQueue = useCallback(async (): Promise<void> => {
    const queue = queues?.[0]
    if (!queue) {
      console.error('No meal plating queue found')
      // TODO: Add proper error handling UI
      return
    }

    setIsLoading(true)
    try {
      // Add participant to the queue
      queue.addParticipant(participant.reference)

      // Update the queue in the backend
      await medplum.updateResource(queue.toFhirResource())

      // Refetch to get updated data
      await refetch()

      setIsDialogOpen(false)
    } catch (error) {
      console.error('Failed to add participant to meal queue:', error)
      // TODO: Add proper error handling UI
    } finally {
      setIsLoading(false)
    }
  }, [queues, participant.reference, medplum, refetch])

  const handleClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    e.preventDefault()
    setIsDialogOpen(true)
  }, [])

  if (isReadOnly) {
    return null
  }

  const queue = queues?.[0]
  const isAlreadyInQueue = queue?.participants.some(
    (p) => p.reference === participant.reference.reference
  )

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        onClick={handleClick}
        title={isAlreadyInQueue ? 'Already in meal queue' : 'Add to meal queue'}
        disabled={isDisabled || isAlreadyInQueue}
      >
        <IconBowlChopsticks />
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogDisplay>
          <DialogHeader>
            <DialogTitle>Add to Meal Queue</DialogTitle>
            <DialogDescription>
              Add <strong>{participant.name}</strong> to the meal plating queue?
            </DialogDescription>
          </DialogHeader>

          <DialogContent>
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleAddToQueue} disabled={isLoading}>
                {isLoading ? 'Adding...' : 'Add to Queue'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogDisplay>
      </Dialog>
    </>
  )
}
